import request from "./config";

class AuthService {
  constructor() {
    this.userCache = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutos
    this.lastFetch = null;
  }

  /**
   * Obtiene el usuario actual
   * @param {boolean} forceRefresh - Forzar actualización desde el servidor
   * @returns {Promise<Object|null>}
   */
  async getCurrentUser(forceRefresh = false) {
    // Verificar si hay token antes de hacer cualquier cosa
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      this.clearCache();
      return null;
    }

    // Si hay cache válido y no se fuerza refresh
    if (!forceRefresh && this.userCache && this.isCacheValid()) {
      return this.userCache;
    }

    try {
      const { data } = await request.get("/users/profile");
      
      if (data.success && data.data && data.data.user) {
        // Combinar user con company para mantener la estructura esperada
        this.userCache = {
          ...data.data.user,
          company: data.data.company
        };
        this.lastFetch = Date.now();
        
        // Actualizar localStorage como backup
        localStorage.setItem("user", JSON.stringify(this.userCache));
        
        // Guardar company por separado si existe
        if (data.data.company) {
          localStorage.setItem("company", JSON.stringify(data.data.company));
        }
        
        return this.userCache;
      }
      
      return null;
    } catch (error) {
      console.error("Error obteniendo usuario:", error);
      
      // Si el error es 401 o 403, limpiar todo
      if (error.status === 401 || error.status === 403) {
        this.clearCache();
        localStorage.removeItem("user");
        localStorage.removeItem("accessToken");
        return null;
      }
      
      // Si falla por otro motivo, usar localStorage como fallback
      const stored = localStorage.getItem("user");
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          return null;
        }
      }
      
      return null;
    }
  }

  /**
   * Verifica si el cache es válido
   */
  isCacheValid() {
    if (!this.lastFetch) return false;
    return (Date.now() - this.lastFetch) < this.cacheTimeout;
  }

  /**
   * Limpia el cache
   */
  clearCache() {
    this.userCache = null;
    this.lastFetch = null;
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  async hasRole(roles) {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return false;

    const user = await this.getCurrentUser();
    if (!user || !user.rol) return false;
    
    const allowedRoles = Array.isArray(roles) ? roles : [roles];
    return allowedRoles.includes(user.rol);
  }

  /**
   * Obtiene el rol del usuario
   */
  async getRole() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return null;

    const user = await this.getCurrentUser();
    return user?.rol || null;
  }
}

export default new AuthService();
